import { useTranslation } from "react-i18next";
import { Badge, Grid, List, Text, Tooltip, useMantineTheme } from "@mantine/core";
import { IconHome, IconPointFilled } from "@tabler/icons-react";
import dayjs from "dayjs";
import { CampaignInquiryQueryDataType } from "src/hooks/campaignInquiries";
import { checkCountryCode } from "src/utils/helpers";

import { FormItem } from "../../../FormItem";
import { RichTextEditor } from "../../../RichTextEditor";

export interface CampaignInquiryGeneralProps {
    campaignInquiry: CampaignInquiryQueryDataType;
}

export const CampaignInquiryGeneral = ({ campaignInquiry }: CampaignInquiryGeneralProps) => {
    const { t } = useTranslation(["common"]);
    const theme = useMantineTheme();

    return (
        <>
            <Grid>
                <Grid.Col xs={12} md={6}>
                    <FormItem label={t("campaign_name")}>
                        {campaignInquiry?.name || <Text color="dimmed">-</Text>}
                    </FormItem>
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                    <FormItem label={t("campaign_timeline_openat")}>
                        {campaignInquiry?.settings?.openAt ? (
                            dayjs(campaignInquiry.settings.openAt).format("DD.MM.YYYY")
                        ) : (
                            <Text color="dimmed">-</Text>
                        )}
                    </FormItem>
                </Grid.Col>
            </Grid>
            <Grid>
                <Grid.Col xs={12} md={6}>
                    <FormItem mb="xs" label={t("campaign_displaytenants")}>
                        <List>
                            {(campaignInquiry?.displayTenants || []).map((tenant, index) => (
                                <List.Item
                                    key={`${index}${tenant?.id}`}
                                    mt="xs"
                                    icon={
                                        campaignInquiry?.mainTenant?.id === tenant?.id ? (
                                            <Tooltip
                                                withArrow
                                                label={
                                                    <>
                                                        <Text weight={500}>{t("campaign_maintenant")}</Text>
                                                        <Text>{t("campaign_maintenant_info")}</Text>
                                                    </>
                                                }
                                            >
                                                <IconHome size="1em" color={theme.colors.orange[6]} />
                                            </Tooltip>
                                        ) : (
                                            <IconPointFilled size="1em" />
                                        )
                                    }
                                >
                                    <Text>{tenant?.name || <Text color="dimmed">-</Text>}</Text>
                                    <Text color="dimmed">{tenant?.domainName}</Text>
                                </List.Item>
                            ))}
                        </List>
                    </FormItem>
                </Grid.Col>
                <Grid.Col xs={12} md={6}>
                    <FormItem label={t("country")}>
                        {campaignInquiry?.tenant?.id && checkCountryCode(campaignInquiry?.tenant?.id) ? t(`country_${campaignInquiry.tenant.id}`) : "-"}
                    </FormItem>
                </Grid.Col>
            </Grid>
            <Grid>
                <Grid.Col xs={12} sm={6} md={6}>
                    <FormItem label={t("campaign_capacity")}>
                        {campaignInquiry?.settings?.capacity || <Text color="dimmed">-</Text>}
                    </FormItem>
                </Grid.Col>
                <Grid.Col xs={12} sm={6} md={6}>
                    <FormItem mb="xs" label={t("campaign_tags")}>
                        {!campaignInquiry?.tags?.length && <Text color="dimmed">-</Text>}
                        {(campaignInquiry?.tags || []).map((tag, index) => (
                            <Badge key={`${index}${tag?.key || tag?.text}`}>{tag?.text || tag?.key}</Badge>
                        ))}
                    </FormItem>
                </Grid.Col>
            </Grid>
            <Grid>
                <Grid.Col xs={12} sm={12} md={12}>
                    <FormItem label={t("campaign_description")}>
                        <RichTextEditor value={campaignInquiry?.description} readOnly />
                    </FormItem>
                </Grid.Col>
            </Grid>
        </>
    );
};
